import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { AuditEventDTO } from '@contracts/activity';
import type { Id, Page } from '@contracts/common';
import type { BoardItem } from '@contracts/workplace';
import { api } from '../../api/client';

/**
 * "Thay đổi 24h" (UX wave D, P1). The board payload carries no updated-at — a card has `version` and `lastChangedBy`
 * but no time — so which cards changed in the last 24 hours is read from the activity log the company already keeps
 * (`GET /api/activity`, module `workplace`, which covers the older `tasks.*` rows too). No API was added for it.
 *
 * A card counts as changed when an event names it as its entity (created, edited, moved, …) or as a reference (a
 * comment or a file on it). The log is read newest first and at most `MAX_PAGES` pages deep; a busier day than that
 * shows the newest changes only. Cards this browser sees change while the page is open are added on top, so a save
 * made a second ago has its dot before the log is read again.
 */

export const CHANGED_WINDOW_MS = 24 * 60 * 60 * 1000;
const PAGE = 100;
const MAX_PAGES = 5;
const CARD_KINDS: ReadonlySet<string> = new Set(['task', 'note']);

export const CHANGES_KEY = ['workplace-changes'] as const;

/** The card ids a page of the activity log names: its entity when that is a card, else the cards it references. */
export function changedIdsOf(events: readonly AuditEventDTO[]): Set<Id> {
  const ids = new Set<Id>();
  for (const event of events) {
    if (CARD_KINDS.has(event.entity.kind)) ids.add(event.entity.id);
    for (const reference of event.references) if (CARD_KINDS.has(reference.kind)) ids.add(reference.id);
  }
  return ids;
}

export async function fetchRecentChanges(now: Date = new Date()): Promise<Id[]> {
  const from = new Date(now.getTime() - CHANGED_WINDOW_MS).toISOString();
  const base = `/api/activity?module=workplace&limit=${PAGE}&from=${encodeURIComponent(from)}`;
  const ids = new Set<Id>();
  let cursor: string | null = null;
  for (let page = 0; page < MAX_PAGES; page += 1) {
    const answer: Page<AuditEventDTO> = await api<Page<AuditEventDTO>>(
      cursor === null ? base : `${base}&cursor=${encodeURIComponent(cursor)}`);
    for (const id of changedIdsOf(answer.items)) ids.add(id);
    cursor = answer.nextCursor;
    if (cursor === null) break;
  }
  return [...ids];
}

const NONE: ReadonlySet<Id> = new Set();

/**
 * The ids changed in the last 24 hours: the log's answer, re-read every minute, plus every card whose version moved
 * (or that appeared) since this page first saw the board. A log that cannot be read leaves only the second part.
 */
export function useChangedIds(items: readonly BoardItem[] | undefined): ReadonlySet<Id> {
  const log = useQuery({
    queryKey: CHANGES_KEY, queryFn: () => fetchRecentChanges(), staleTime: 30_000, refetchInterval: 60_000, retry: false,
  });
  const [first, setFirst] = useState<ReadonlyMap<Id, number> | null>(null);
  useEffect(() => {
    if (first === null && items !== undefined) setFirst(new Map(items.map((card) => [card.id, card.version])));
  }, [first, items]);
  return useMemo(() => {
    const out = new Set<Id>(log.data ?? []);
    if (first !== null && items !== undefined) {
      for (const card of items) if (first.get(card.id) !== card.version) out.add(card.id);
    }
    return out.size === 0 ? NONE : out;
  }, [log.data, items, first]);
}

/** The changed ids to every card of every view, for the 8 px blue dot. */
export const ChangedContext = createContext<ReadonlySet<Id>>(NONE);
export const useChanged = (id: Id): boolean => useContext(ChangedContext).has(id);
